import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import api from '../../services/api';
import useAppStore from '../../store/app.store';

const COLUNAS = [
  { key: 'ideia', label: '💡 Ideias', cor: '#A78BFA' },
  { key: 'planejamento', label: 'Planejamento', cor: '#38BDF8' },
  { key: 'em_andamento', label: 'Em Andamento', cor: '#FBBF24' },
  { key: 'concluido', label: 'Concluído', cor: '#34D399' },
];
const PRIORIDADE = { baixa: ['Baixa', '#94A3B8'], media: ['Média', '#38BDF8'], alta: ['Alta', '#F87171'] };
const dataBR = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : null);

function BarraProgresso({ pct, cor = '#6C4CF5', altura = 6 }) {
  return (
    <div style={{ height: altura, background: 'rgba(255,255,255,.08)', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: cor, borderRadius: 999, transition: 'width .3s' }} />
    </div>
  );
}

function CardProjeto({ p, onClick, onDragStart }) {
  const [pl, pc] = PRIORIDADE[p.prioridade] || PRIORIDADE.media;
  return (
    <div draggable
      onDragStart={() => onDragStart(p.id)}
      onClick={() => onClick(p)}
      className="cursor-grab active:cursor-grabbing"
      style={{ background: '#19243A', border: '1px solid #2A3652', borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F4', flex: 1 }}>{p.nome}</p>
        <span style={{ fontSize: 10, fontWeight: 700, color: pc, background: `${pc}18`, padding: '2px 7px', borderRadius: 5, flexShrink: 0 }}>{pl}</span>
      </div>
      {p.total_tarefas > 0 ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748B', marginBottom: 4 }}>
            <span>{p.tarefas_concluidas}/{p.total_tarefas} tarefas</span>
            <span style={{ fontWeight: 700, color: '#94A3B8' }}>{p.progresso}%</span>
          </div>
          <BarraProgresso pct={p.progresso} cor={p.progresso === 100 ? '#34D399' : '#6C4CF5'} />
        </div>
      ) : (
        <p style={{ fontSize: 10, color: '#475569', marginTop: 8 }}>Sem tarefas ainda</p>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 10, color: '#64748B' }}>
        <span>{p.responsavel_nome || 'Sem responsável'}</span>
        {p.data_alvo && <span>🎯 {dataBR(p.data_alvo)}</span>}
      </div>
    </div>
  );
}

export default function Projetos() {
  const usuario = useAppStore((s) => s.usuario);
  const [projetos, setProjetos] = useState([]);
  const [minhasTarefas, setMinhasTarefas] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [modalNovo, setModalNovo] = useState(false);
  const [detalheId, setDetalheId] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [mostrarArquivados, setMostrarArquivados] = useState(false);

  const carregar = useCallback(() => {
    api.get('/projetos').then((r) => setProjetos(r.data || [])).catch(() => toast.error('Erro ao carregar projetos.'));
    api.get('/projetos/minhas-tarefas').then((r) => setMinhasTarefas(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    carregar();
    api.get('/usuarios/equipe').then((r) => setEquipe(r.data || [])).catch(() => {});
  }, [carregar]);

  const abrirDetalhe = useCallback((id) => {
    setDetalheId(id);
    api.get(`/projetos/${id}`).then((r) => setDetalhe(r.data)).catch(() => toast.error('Erro ao abrir projeto.'));
  }, []);

  const recarregarDetalhe = () => { if (detalheId) abrirDetalhe(detalheId); carregar(); };

  const handleDrop = async (novoStatus) => {
    const id = draggingId;
    setDraggingId(null); setDragOverCol(null);
    if (!id) return;
    const p = projetos.find((x) => x.id === id);
    if (!p || p.status === novoStatus) return;
    setProjetos((prev) => prev.map((x) => (x.id === id ? { ...x, status: novoStatus } : x)));
    try {
      await api.put(`/projetos/${id}`, { status: novoStatus });
      if (novoStatus === 'concluido') toast.success('Projeto concluído! 🎉');
    } catch {
      toast.error('Erro ao mover projeto.');
      carregar();
    }
  };

  const grupos = { ideia: [], planejamento: [], em_andamento: [], concluido: [] };
  projetos.forEach((p) => {
    if (p.status === 'pausado') grupos.em_andamento.push(p); // pausados ficam visíveis na coluna de andamento
    else if (grupos[p.status]) grupos[p.status].push(p);
  });
  const arquivados = projetos.filter((p) => p.status === 'arquivado');

  return (
    <div>
      <PageHeader
        title="Projetos"
        subtitle="Do banco de ideias à entrega — etapas, delegação e progresso"
        actions={<button onClick={() => setModalNovo(true)} className="btn-primary">+ Novo Projeto</button>}
      />

      {/* Minhas tarefas pendentes */}
      {minhasTarefas.length > 0 && (
        <div style={{ background: 'rgba(108,76,245,.08)', border: '1px solid rgba(108,76,245,.25)', borderRadius: 14, padding: '14px 18px', marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: '#A78BFA', marginBottom: 10 }}>
            📋 Minhas tarefas de projetos
            <span style={{ marginLeft: 8, background: '#6C4CF5', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '1px 7px' }}>{minhasTarefas.length}</span>
          </h3>
          <div className="resp-grid-3" style={{ gap: 8 }}>
            {minhasTarefas.slice(0, 6).map((t) => {
              const atrasada = t.data_prazo && new Date(t.data_prazo) < new Date();
              return (
                <div key={t.id} onClick={() => abrirDetalhe(t.projeto_id)}
                  style={{ background: '#19243A', border: `1px solid ${atrasada ? 'rgba(248,113,113,.4)' : '#2A3652'}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#E2E8F4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.titulo}</p>
                  <p style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                    {t.projeto_nome} · {t.etapa_nome}
                    {t.data_prazo && <span style={{ color: atrasada ? '#F87171' : '#64748B' }}> · {atrasada ? 'atrasada!' : dataBR(t.data_prazo)}</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUNAS.map((col) => (
          <div key={col.key}
            className="kanban-col min-h-[220px]"
            style={{ borderTop: `3px solid ${col.cor}`, background: dragOverCol === col.key ? 'rgba(108,76,245,.06)' : undefined, borderRadius: 12 }}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={(e) => { e.preventDefault(); handleDrop(col.key); }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: col.cor }}>{col.label}</h3>
              <span className="text-xs rounded-full px-2 py-0.5 font-semibold" style={{ background: `${col.cor}18`, color: col.cor }}>{grupos[col.key].length}</span>
            </div>
            {grupos[col.key].map((p) => (
              <CardProjeto key={p.id} p={p} onClick={(x) => abrirDetalhe(x.id)} onDragStart={setDraggingId} />
            ))}
            {grupos[col.key].length === 0 && (
              <p style={{ fontSize: 11, color: '#475569', textAlign: 'center', padding: '20px 0' }}>
                {col.key === 'ideia' ? 'Anote aqui o que você quer criar um dia.' : 'Arraste projetos para cá.'}
              </p>
            )}
          </div>
        ))}
      </div>

      {arquivados.length > 0 && (
        <button onClick={() => setMostrarArquivados(!mostrarArquivados)}
          style={{ fontSize: 12, color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', marginTop: 8 }}>
          {mostrarArquivados ? '▾' : '▸'} Arquivados ({arquivados.length})
        </button>
      )}
      {mostrarArquivados && (
        <div className="resp-grid-3" style={{ gap: 8, marginTop: 8, opacity: 0.6 }}>
          {arquivados.map((p) => <CardProjeto key={p.id} p={p} onClick={(x) => abrirDetalhe(x.id)} onDragStart={() => {}} />)}
        </div>
      )}

      <ModalNovoProjeto open={modalNovo} onClose={() => setModalNovo(false)} equipe={equipe}
        onCriado={(p) => { setModalNovo(false); carregar(); abrirDetalhe(p.id); }} />

      {detalheId && (
        <ModalDetalheProjeto
          projeto={detalhe}
          equipe={equipe}
          usuario={usuario}
          onClose={() => { setDetalheId(null); setDetalhe(null); carregar(); }}
          onMudou={recarregarDetalhe}
        />
      )}
    </div>
  );
}

// ─── Novo projeto ─────────────────────────────────────────────────────────────
function ModalNovoProjeto({ open, onClose, equipe, onCriado }) {
  const [form, setForm] = useState({ nome: '', descricao: '', status: 'ideia', prioridade: 'media', responsavel_id: '', data_alvo: '' });
  const [salvando, setSalvando] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const { data } = await api.post('/projetos', { ...form, responsavel_id: form.responsavel_id || undefined, data_alvo: form.data_alvo || undefined });
      toast.success(form.status === 'ideia' ? 'Ideia anotada! 💡' : 'Projeto criado!');
      setForm({ nome: '', descricao: '', status: 'ideia', prioridade: 'media', responsavel_id: '', data_alvo: '' });
      onCriado(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar projeto.');
    } finally { setSalvando(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Novo projeto" size="md">
      <form onSubmit={salvar} className="space-y-3">
        <div>
          <label className="form-label">Nome *</label>
          <input className="form-input" value={form.nome} onChange={(e) => set('nome', e.target.value)} required autoFocus
            placeholder="Ex.: Portal do cliente, Rebranding, Novo produto…" />
        </div>
        <div>
          <label className="form-label">Descrição / visão</label>
          <textarea className="form-input" rows={3} value={form.descricao} onChange={(e) => set('descricao', e.target.value)}
            placeholder="O que é essa ideia? Qual problema resolve?" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Começa como</label>
            <select className="form-input" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="ideia">💡 Ideia (sem compromisso)</option>
              <option value="planejamento">Planejamento</option>
              <option value="em_andamento">Em andamento</option>
            </select>
          </div>
          <div>
            <label className="form-label">Prioridade</label>
            <select className="form-input" value={form.prioridade} onChange={(e) => set('prioridade', e.target.value)}>
              <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
            </select>
          </div>
          <div>
            <label className="form-label">Responsável</label>
            <select className="form-input" value={form.responsavel_id} onChange={(e) => set('responsavel_id', e.target.value)}>
              <option value="">Eu mesmo</option>
              {equipe.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Prazo alvo</label>
            <input type="date" className="form-input" value={form.data_alvo} onChange={(e) => set('data_alvo', e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn-primary flex-1" disabled={salvando}>{salvando ? 'Salvando…' : 'Criar projeto'}</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Detalhe do projeto: etapas + tarefas + delegação ────────────────────────
function ModalDetalheProjeto({ projeto: p, equipe, usuario, onClose, onMudou }) {
  const [novaEtapa, setNovaEtapa] = useState('');
  const [novaTarefa, setNovaTarefa] = useState({}); // por etapa: {titulo, responsavel_id, data_prazo}
  const isAdmin = usuario?.nivel_acesso <= 2;

  if (!p) return <Modal open onClose={onClose} title="Carregando…" size="xl"><p className="text-sm text-gray-500 py-6 text-center">Abrindo projeto…</p></Modal>;

  const mudarCampo = async (campos) => {
    try { await api.put(`/projetos/${p.id}`, campos); onMudou(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar.'); }
  };

  const addEtapa = async () => {
    if (!novaEtapa.trim()) return;
    try {
      await api.post(`/projetos/${p.id}/etapas`, { nome: novaEtapa.trim() });
      setNovaEtapa('');
      onMudou();
    } catch { toast.error('Erro ao criar etapa.'); }
  };

  const addTarefa = async (etapaId) => {
    const t = novaTarefa[etapaId];
    if (!t?.titulo?.trim()) return;
    try {
      await api.post(`/projetos/etapas/${etapaId}/tarefas`, {
        titulo: t.titulo.trim(), responsavel_id: t.responsavel_id || undefined, data_prazo: t.data_prazo || undefined,
      });
      setNovaTarefa((prev) => ({ ...prev, [etapaId]: { titulo: '', responsavel_id: t.responsavel_id, data_prazo: '' } }));
      if (t.responsavel_id && t.responsavel_id !== usuario.id) toast.success('Tarefa delegada — o responsável foi notificado! 🔔');
      onMudou();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao criar tarefa.'); }
  };

  const toggleTarefa = async (tarefa) => {
    try {
      await api.patch(`/projetos/tarefas/${tarefa.id}/concluir`, { concluida: !tarefa.concluida });
      onMudou();
    } catch { toast.error('Erro ao atualizar tarefa.'); }
  };

  const excluirProjeto = async () => {
    if (!window.confirm(`Excluir o projeto "${p.nome}" e todas as etapas/tarefas?`)) return;
    try { await api.delete(`/projetos/${p.id}`); toast.success('Projeto excluído.'); onClose(); }
    catch { toast.error('Erro ao excluir.'); }
  };

  return (
    <Modal open onClose={onClose} title={p.nome} size="xl">
      {/* Cabeçalho: status, prioridade, responsável, progresso */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div>
          <label className="form-label">Status</label>
          <select className="form-input" value={p.status} onChange={(e) => mudarCampo({ status: e.target.value })}>
            <option value="ideia">💡 Ideia</option><option value="planejamento">Planejamento</option>
            <option value="em_andamento">Em andamento</option><option value="pausado">Pausado</option>
            <option value="concluido">Concluído</option><option value="arquivado">Arquivado</option>
          </select>
        </div>
        <div>
          <label className="form-label">Prioridade</label>
          <select className="form-input" value={p.prioridade} onChange={(e) => mudarCampo({ prioridade: e.target.value })}>
            <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
          </select>
        </div>
        <div>
          <label className="form-label">Responsável</label>
          <select className="form-input" value={p.responsavel_id || ''} onChange={(e) => mudarCampo({ responsavel_id: e.target.value })}>
            <option value="">—</option>
            {equipe.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Prazo alvo</label>
          <input type="date" className="form-input" value={p.data_alvo?.slice(0, 10) || ''} onChange={(e) => mudarCampo({ data_alvo: e.target.value })} />
        </div>
      </div>

      {p.descricao && <p className="text-sm text-gray-600 mb-4 whitespace-pre-wrap">{p.descricao}</p>}

      <div className="mb-5">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progresso geral · {p.tarefas_concluidas}/{p.total_tarefas} tarefas</span>
          <span className="font-bold text-gray-700">{p.progresso}%</span>
        </div>
        <div style={{ height: 10, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${p.progresso}%`, background: p.progresso === 100 ? '#34D399' : '#6C4CF5', borderRadius: 999, transition: 'width .3s' }} />
        </div>
      </div>

      {/* Etapas */}
      <div className="space-y-3">
        {p.etapas.map((et, idx) => (
          <div key={et.id} className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-gray-800">
                {et.progresso === 100 && et.tarefas.length > 0 ? '✅' : `${idx + 1}.`} {et.nome}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{et.tarefas.filter((t) => t.concluida).length}/{et.tarefas.length}</span>
                {isAdmin && (
                  <button onClick={async () => {
                    if (!window.confirm(`Excluir a etapa "${et.nome}" e suas tarefas?`)) return;
                    await api.delete(`/projetos/etapas/${et.id}`); onMudou();
                  }} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                )}
              </div>
            </div>
            {et.tarefas.length > 0 && (
              <div className="mb-2" style={{ height: 5, background: '#F3F4F6', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${et.progresso}%`, background: et.progresso === 100 ? '#34D399' : '#38BDF8', borderRadius: 999 }} />
              </div>
            )}

            {/* Tarefas */}
            <div className="space-y-1.5">
              {et.tarefas.map((t) => {
                const atrasada = !t.concluida && t.data_prazo && new Date(t.data_prazo) < new Date();
                return (
                  <div key={t.id} className="flex items-center gap-2 text-sm group">
                    <input type="checkbox" checked={t.concluida} onChange={() => toggleTarefa(t)}
                      className="w-4 h-4 cursor-pointer accent-violet-600" />
                    <span className={`flex-1 ${t.concluida ? 'line-through text-gray-400' : 'text-gray-700'}`}>{t.titulo}</span>
                    {atrasada && <span className="text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">atrasada</span>}
                    {!atrasada && t.data_prazo && !t.concluida && <span className="text-[10px] text-gray-400">{dataBR(t.data_prazo)}</span>}
                    {t.responsavel_nome && (
                      <span className="text-[10px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-full" title={t.responsavel_nome}>
                        {t.responsavel_nome.split(' ').slice(0, 2).map((n) => n[0]).join('')}
                      </span>
                    )}
                    {isAdmin && (
                      <button onClick={async () => { await api.delete(`/projetos/tarefas/${t.id}`); onMudou(); }}
                        className="text-gray-300 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100">✕</button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Nova tarefa nesta etapa */}
            {isAdmin && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                <input className="form-input flex-1 min-w-[140px]" style={{ padding: '6px 10px', fontSize: 13 }}
                  placeholder="+ Nova microtarefa…"
                  value={novaTarefa[et.id]?.titulo || ''}
                  onChange={(e) => setNovaTarefa((prev) => ({ ...prev, [et.id]: { ...prev[et.id], titulo: e.target.value } }))}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTarefa(et.id))} />
                <select className="form-input w-auto" style={{ padding: '6px 8px', fontSize: 12 }}
                  value={novaTarefa[et.id]?.responsavel_id || ''}
                  onChange={(e) => setNovaTarefa((prev) => ({ ...prev, [et.id]: { ...prev[et.id], responsavel_id: e.target.value } }))}>
                  <option value="">Delegar para…</option>
                  {equipe.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
                <input type="date" className="form-input w-auto" style={{ padding: '6px 8px', fontSize: 12 }}
                  value={novaTarefa[et.id]?.data_prazo || ''}
                  onChange={(e) => setNovaTarefa((prev) => ({ ...prev, [et.id]: { ...prev[et.id], data_prazo: e.target.value } }))} />
                <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => addTarefa(et.id)}>Adicionar</button>
              </div>
            )}
          </div>
        ))}

        {/* Nova etapa */}
        {isAdmin && (
          <div className="flex gap-2">
            <input className="form-input flex-1" placeholder="+ Nova etapa (ex.: Levantamento, Desenvolvimento, Lançamento…)"
              value={novaEtapa} onChange={(e) => setNovaEtapa(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEtapa())} />
            <button className="btn-primary" onClick={addEtapa}>Criar etapa</button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex justify-end mt-5 pt-4 border-t border-gray-100">
          <button onClick={excluirProjeto} className="text-xs text-red-500 hover:text-red-700 font-medium">Excluir projeto</button>
        </div>
      )}
    </Modal>
  );
}
